import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useListTenantLocationsQuery } from 'rtk/org-admin/location.slice';
import ShareRequestAccessFields from 'components/common/ShareRequestAccessFields';
import {
  SHARE_ACCESS_FORM_DEFAULTS,
  validateShareRequestAccess
} from 'util/shareRequestAccess';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaultFormValues = {
  email: '',
  locationGuid: '',
  ...SHARE_ACCESS_FORM_DEFAULTS
};

const AssociationRequestModal = ({
  show,
  onHide,
  title,
  emailLabel,
  emailPlaceholder,
  showAccessType,
  sending,
  onSubmit,
  showLocationDropdown,
  locationLabel,
  cancelLabel,
  submitLabel
}) => {
  const userMaster = useSelector(state => state.app.userMaster);
  const tenantGuid = userMaster?.TenantGUID || null;
  const navigate = useNavigate();

  const formMethods = useForm({ defaultValues: defaultFormValues });
  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors }
  } = formMethods;

  const {
    data: locationsRes,
    isFetching,
    isLoading,
    isError,
    error: locationsQueryError
  } = useListTenantLocationsQuery(
    {
      tenantGuid,
      indexStart: 0,
      maxResults: 500
    },
    { skip: !tenantGuid || !showLocationDropdown }
  );

  const locationsLoading = isFetching || isLoading;
  const locationsError = isError
    ? locationsQueryError?.Message || 'Failed to load locations'
    : null;

  const sortedLocations = useMemo(() => {
    const arr = Array.isArray(locationsRes?.Objects)
      ? locationsRes.Objects
      : [];
    return [...arr].sort((a, b) =>
      String(a?.DisplayName || '').localeCompare(String(b?.DisplayName || ''))
    );
  }, [locationsRes]);

  const noLocations =
    !!tenantGuid &&
    !locationsLoading &&
    !locationsError &&
    sortedLocations.length === 0;

  // With no saved locations, offer to create one: close this dialog and open the Location page
  // with its Add panel already showing.
  const goToAddLocation = () => {
    onHide();
    navigate('/admin/location', { state: { openAddLocation: true } });
  };

  useEffect(() => {
    if (show) {
      reset(defaultFormValues);
      clearErrors();
    }
  }, [show, reset, clearErrors]);

  // Field validation messages are shown as a toast (first failing field) rather than inline.
  const onInvalid = formErrors => {
    const message =
      formErrors.email?.message || formErrors.locationGuid?.message;
    if (!message) return;
    toast.info(message, {
      theme: 'colored',
      position: 'bottom-center',
      toastId: 'association-request-validation'
    });
  };

  const onFormSubmit = handleSubmit(async data => {
    if (showLocationDropdown && noLocations) {
      toast.info('Please add a location first', {
        theme: 'colored',
        position: 'bottom-center',
        toastId: 'association-no-location'
      });
      return;
    }
    if (showAccessType) {
      const accessValidation = validateShareRequestAccess(
        data.accessType,
        data.scopeFromDate,
        data.scopeToDate
      );
      if (!accessValidation.valid) {
        setError('scopeFromDate', {
          type: 'manual',
          message: accessValidation.message
        });
        return;
      }
    }

    await onSubmit({
      email: (data.email || '').trim(),
      locationGuid: data.locationGuid || '',
      accessType: data.accessType,
      scopeFromDate: data.scopeFromDate || '',
      scopeToDate: data.scopeToDate || ''
    });
  }, onInvalid);

  return (
    <Modal show={show} onHide={() => !sending && onHide()} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <FormProvider {...formMethods}>
        <Form onSubmit={onFormSubmit} noValidate>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                {emailLabel} <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                placeholder={emailPlaceholder}
                disabled={sending}
                {...register('email', {
                  required: 'Please enter an email address.',
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: 'Please enter a valid email address.'
                  }
                })}
              />
            </Form.Group>

            {showLocationDropdown && (
              <Form.Group className="mb-3">
                <Form.Label>
                  {locationLabel} <span className="text-danger">*</span>
                </Form.Label>
                {noLocations ? (
                  <div
                    className="border rounded-3 p-3 text-center bg-light"
                    style={{ borderStyle: 'dashed' }}
                  >
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="text-primary mb-2"
                      size="lg"
                    />
                    <p className="fs--1 text-700 mb-2">
                      You don&apos;t have any locations yet. Add one to send a
                      connection request.
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      type="button"
                      onClick={goToAddLocation}
                      disabled={sending}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" />
                      Add location
                    </Button>
                  </div>
                ) : (
                  <>
                    <Form.Select
                      disabled={sending || locationsLoading || !tenantGuid}
                      isInvalid={!!errors.locationGuid}
                      {...register('locationGuid', {
                        required: 'Please select a location.'
                      })}
                    >
                      <option value="">
                        {tenantGuid
                          ? 'Select location'
                          : 'Tenant not available'}
                      </option>
                      {sortedLocations.map(l => (
                        <option key={l?.GUID} value={l?.GUID}>
                          {l?.DisplayName || l?.GUID}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.locationGuid?.message}
                    </Form.Control.Feedback>
                  </>
                )}
                <div className="d-flex align-items-center gap-2 mt-2">
                  {locationsLoading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <small className="text-600">Loading locations…</small>
                    </>
                  ) : null}
                  {locationsError ? (
                    <small className="text-danger">{locationsError}</small>
                  ) : null}
                </div>
              </Form.Group>
            )}

            {showAccessType && (
              <ShareRequestAccessFields
                control={control}
                errors={errors}
                disabled={sending}
                accessTypeId="association-request-access"
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              type="button"
              onClick={() => onHide()}
              disabled={sending}
            >
              {cancelLabel}
            </Button>
            <Button variant="primary" type="submit" disabled={sending}>
              {sending ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : null}
              {submitLabel}
            </Button>
          </Modal.Footer>
        </Form>
      </FormProvider>
    </Modal>
  );
};

AssociationRequestModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  title: PropTypes.string,
  emailLabel: PropTypes.string,
  emailPlaceholder: PropTypes.string,
  showAccessType: PropTypes.bool,
  sending: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  showLocationDropdown: PropTypes.bool,
  locationLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  submitLabel: PropTypes.string
};

AssociationRequestModal.defaultProps = {
  title: 'Send Request',
  emailLabel: 'Email address',
  emailPlaceholder: 'email@example.com',
  showAccessType: true,
  sending: false,
  showLocationDropdown: false,
  locationLabel: 'Location',
  cancelLabel: 'Cancel',
  submitLabel: 'Send Request'
};

export default AssociationRequestModal;
